import React, { useState, useEffect, useRef } from 'react';
import './NumberRange.css';
import { roundVolumeInput, roundPriceInput } from '../../../utils/displayRounding';
import { getNumberRangeStep, isUnlimitedDecimalType, truncateToDecimalPlaces, applyNumericBlur } from './NumberRange.helpers';

const NumberRange = ({
    value: initialValue,
    onChange,
    onBlur: onBlurProp,
    max,
    min,
    error,
    errorMessage,
    placeholder,
    isRequiredNeeded = false,
    name,
    disabled,
    allowDecimals = false,
    decimalPlaces = 0,
    inputType = 'integer',
}) => {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);
    const justRounded = useRef(false);

    useEffect(() => {
        // Don't sync if we just rounded (to prevent overriding the formatted value)
        // Also, don't format values coming from props if they're simple integers
        if (!justRounded.current && initialValue !== value) {
            const valueToSet = initialValue;
            setValue(valueToSet);
            if (inputRef.current && initialValue !== null && initialValue !== undefined) {
                inputRef.current.value = valueToSet;
            }
        }
        if (justRounded.current) {
            setTimeout(() => {
                justRounded.current = false;
            }, 200);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue, allowDecimals]);

    const handleInput = (e) => {
        let newValue = e.target.value;
        
        // For Volume and Price, allow unlimited decimal places while typing
        // For other types, truncate decimals to max allowed places, but don't round while typing
        if (allowDecimals && newValue !== '' && newValue !== '.') {
            if (newValue.includes('.')) {
                const parts = newValue.split('.');
                const decimalPart = parts[1] || '';
                
                // Only truncate if NOT volume or price type
                if (!isUnlimitedDecimalType(inputType) && decimalPart.length > decimalPlaces) {
                    const truncatedValue = truncateToDecimalPlaces(newValue, decimalPlaces);
                    e.target.value = truncatedValue;
                    setValue(truncatedValue);
                    onChange(truncatedValue);
                }
            }
        }
    };

    const applyRestrictedDecimalValue = (newValue) => {
        let finalValue = newValue;
        if (newValue.includes('.')) {
            const parts = newValue.split('.');
            const decimalPart = parts[1] || '';
            if (decimalPart.length > decimalPlaces) {
                finalValue = truncateToDecimalPlaces(newValue, decimalPlaces);
                if (inputRef.current) {
                    inputRef.current.value = finalValue;
                }
            }
        }
        const decimalRegex = new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`);
        if (!decimalRegex.test(finalValue)) {
            if (inputRef.current) {
                inputRef.current.value = value || '';
            }
            return null;
        }
        return finalValue;
    };

    const applyUnlimitedDecimalValue = (finalValue) => {
        const numberRegex = /^\d*\.?\d*$/;
        if (!numberRegex.test(finalValue)) {
            if (inputRef.current) {
                inputRef.current.value = value || '';
            }
            return null;
        }
        return finalValue;
    };

    const handleIntegerNumber = (newValue) => {
        if (newValue.startsWith('0') && newValue.length > 1 && !newValue.startsWith('0.')) {
            newValue = newValue.slice(1);
        }
        if (newValue === '' || (Number(newValue) >= min && Number(newValue) <= max)) {
            setValue(newValue);
            onChange(newValue);
        }
    };

    const handleNumber = (e) => {
        let newValue = e.target.value;
        
        // Allow empty input
        if (newValue === '') {
            setValue('');
            onChange('');
            return;
        }

        if (!allowDecimals) {
            handleIntegerNumber(newValue);
            return;
        }

        const finalValue = isUnlimitedDecimalType(inputType)
            ? applyUnlimitedDecimalValue(newValue)
            : applyRestrictedDecimalValue(newValue);
        if (finalValue === null) {
            return;
        }
        setValue(finalValue);
        onChange(finalValue);
    };

    const applyRoundedBlurValue = (e, rounded) => {
        justRounded.current = true;
        setValue(rounded);
        onChange(rounded);
        if (e && e.target && rounded) {
            e.target.value = rounded;
        }
        if (onBlurProp) {
            onBlurProp(e);
        }
    };

    const handleBlur = (e) => {
        if (value !== '' && value !== null && value !== undefined && value !== '.') {
            applyNumericBlur({
                e,
                value,
                inputType,
                onBlurProp,
                applyRoundedBlurValue,
                roundVolumeInput,
                roundPriceInput,
            });
        } else if (onBlurProp) {
            onBlurProp(e);
        }
    };


    return (
        <div>
            <div className='position-relative'>
                <input
                    ref={inputRef}
                    type={allowDecimals ? 'text' : 'number'}
                    step={getNumberRangeStep(allowDecimals, inputType)}
                    value={value || ''}
                    onInput={handleInput}
                    onChange={handleNumber}
                    onBlur={handleBlur}
                    className='form-control form-control-sm border-none'
                    placeholder={placeholder}
                    required={isRequiredNeeded}
                    name={name}
                    disabled={disabled}
                />
                {error && <p className='text-error-700 font-b3 font-regular mt-2'>{errorMessage}</p>}
            </div>
        </div>
    );
};

export default NumberRange;
